import React, { useCallback, useState } from 'react';
import { Button } from 'core/components';
import { inject, observer } from 'mobx-react';
import type { Query } from '@kentik/ui-shared/query/query.types';

interface ViewInDashboardButtonProps {
  query: Query;
  title?: string;
  $boards?: {
    collection: {
      forge: (attrs: Record<string, unknown>) => {
        id: string | number;
        save: () => Promise<void>;
        addPanel: (panel: Record<string, unknown>) => Promise<{ id: string | number }>;
      };
    };
  };
}

/**
 * Button that creates a new dashboard with a UDE panel pre-configured with the given query.
 * Opens the new board in a new browser tab.
 */
function ViewInDashboardButton({ query, title, $boards }: ViewInDashboardButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async () => {
    if (!query || !$boards || loading) {
      return;
    }

    setLoading(true);
    try {
      // Create a new board
      const board = $boards.collection.forge({
        title: `AI Query: ${title || 'UDE Query'}`,
        description: 'Created from Kaizen AI chat',
        share_level: 'self'
      });

      await board.save();

      // Add a UDE panel with the query
      const panel = await board.addPanel({
        type: 'ude',
        title: title || 'UDE Query',
        config: {
          data: query,
          chart: { type: query.viz?.type || 'stackedArea', enable: true },
          table: { enable: true }
        }
      });

      // Open the board panel in a new tab
      const url = `/v4/library/boards/${board.id}/panel/${panel.id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to create dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [query, title, loading, $boards]);

  return <Button small minimal icon="new-grid-item" text="Add to Dashboard" loading={loading} onClick={handleClick} />;
}

export default inject('$boards')(observer(ViewInDashboardButton));
